/**
 * Native Bridge for iOS WKWebView Integration
 *
 * Provides a unified API that works on both web and iOS native app.
 * On iOS, messages are sent via WKScriptMessageHandler.
 * On web, functions are no-ops or use web equivalents.
 */

// Bare i18next singleton (initialized by src/i18n.js at app startup); avoids
// pulling the full i18n/react-i18next setup into this module.
import i18next from 'i18next';

// Check if running in iOS native app
export const isNativeApp = () => {
  return !!(window.webkit?.messageHandlers?.saveImage);
};

// Check if running on iOS (native or Safari)
export const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

// Check if running in a mobile browser (non-native)
export const isMobileWeb = () => {
  if (isNativeApp()) {
    return false;
  }
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
};

/**
 * Whether digital purchases may be offered on this platform.
 *
 * App Store Review Guideline 3.1.1 requires digital content unlocked inside an
 * iOS app -- image credits and PDF downloads, here -- to be sold through
 * Apple's In-App Purchase. Sending users out to Stripe in Safari to buy them is
 * a rejection, so until StoreKit is wired up the iOS app does not offer them at
 * all, and must not link out to or hint at buying them elsewhere.
 *
 * Physical print orders are deliberately NOT covered by this: goods shipped to
 * the user are exempt from IAP and correctly use the external checkout.
 */
export const canPurchaseDigitalGoods = () => !isNativeApp();

// Determine checkout platform for redirect handling
export const getCheckoutPlatform = () => {
  if (isNativeApp()) {
    return 'ios-app';
  }
  if (isMobileWeb()) {
    return 'mobile-web';
  }
  return 'desktop-web';
};

/**
 * Native Bridge API
 * All methods are safe to call on web - they will be no-ops if not in native app
 */
const NativeBridge = {
  /**
   * Check if native bridge is available
   */
  isAvailable: () => isNativeApp(),

  /**
   * Save an image to the device's photo library
   * @param {string} imageUrl - URL of the image to save
   * @returns {Promise<boolean>} - True if native handled, false otherwise
   */
  saveImage: async (imageUrl) => {
    if (window.webkit?.messageHandlers?.saveImage) {
      window.webkit.messageHandlers.saveImage.postMessage(imageUrl);
      return true;
    }
    // Web fallback: trigger download
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vision-board-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      console.error('Failed to save image:', error);
      return false;
    }
  },

  /**
   * Share an image using native share sheet
   * @param {string} imageUrl - URL of the image to share
   * @param {string} title - Optional title for share
   * @returns {Promise<boolean>} - True if native handled, false otherwise
   */
  shareImage: async (imageUrl, title = i18next.t('share.defaultShareTitle')) => {
    if (window.webkit?.messageHandlers?.shareImage) {
      window.webkit.messageHandlers.shareImage.postMessage({ url: imageUrl, title });
      return true;
    }
    // Web fallback: use Web Share API if available
    if (navigator.share) {
      try {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const file = new File([blob], 'vision-board.png', { type: 'image/png' });
        await navigator.share({
          title,
          files: [file]
        });
        return true;
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Share failed:', error);
        }
        return false;
      }
    }
    return false;
  },

  /**
   * Set image as device wallpaper (iOS guided flow)
   * @param {string} imageUrl - URL of the image
   * @returns {boolean} - True if native handled
   */
  setWallpaper: (imageUrl) => {
    if (window.webkit?.messageHandlers?.setWallpaper) {
      window.webkit.messageHandlers.setWallpaper.postMessage(imageUrl);
      return true;
    }
    // No web equivalent
    return false;
  },

  /**
   * Schedule a local notification reminder
   * @param {Object} options - Notification options
   * @param {string} options.title - Notification title
   * @param {string} options.body - Notification body
   * @param {string} options.time - Time in HH:MM format
   * @returns {boolean} - True if native handled
   */
  scheduleReminder: ({ title, body, time }) => {
    if (window.webkit?.messageHandlers?.scheduleReminder) {
      window.webkit.messageHandlers.scheduleReminder.postMessage({ title, body, time });
      return true;
    }
    // Web fallback: use Notification API for immediate notification
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, { body });
      return true;
    }
    return false;
  },

  /**
   * Update the home screen widget with new content
   * @param {Object} payload - Widget data
   * @param {string} payload.imageUrl - Image URL for widget
   * @param {string} payload.title - Title text
   * @param {string} payload.affirmation - Affirmation text
   * @returns {boolean} - True if native handled
   */
  updateWidget: (payload) => {
    if (window.webkit?.messageHandlers?.updateWidget) {
      window.webkit.messageHandlers.updateWidget.postMessage(JSON.stringify(payload));
      return true;
    }
    // No web equivalent
    return false;
  },

  /**
   * Request the current auth token from native storage
   * Used when native app handles authentication
   * @returns {Promise<string|null>} - Auth token or null
   */
  getAuthToken: () => {
    return new Promise((resolve) => {
      if (window.webkit?.messageHandlers?.getAuthToken) {
        // Set up a callback for native to call
        window.nativeAuthCallback = (token) => {
          resolve(token);
          delete window.nativeAuthCallback;
        };
        window.webkit.messageHandlers.getAuthToken.postMessage({});
        // Timeout after 5 seconds
        setTimeout(() => {
          if (window.nativeAuthCallback) {
            delete window.nativeAuthCallback;
            resolve(null);
          }
        }, 5000);
      } else {
        resolve(null);
      }
    });
  },

  /**
   * Request native login flow
   * @returns {boolean} - True if native handled
   */
  requestLogin: () => {
    if (window.webkit?.messageHandlers?.requestLogin) {
      window.webkit.messageHandlers.requestLogin.postMessage({});
      return true;
    }
    return false;
  },

  /**
   * Request native logout
   * @returns {boolean} - True if native handled
   */
  requestLogout: () => {
    if (window.webkit?.messageHandlers?.requestLogout) {
      window.webkit.messageHandlers.requestLogout.postMessage({});
      return true;
    }
    return false;
  },

  /**
   * Whether the user has consented to sending prompts to the AI provider.
   *
   * On iOS the native shell owns this decision (guideline 5.1.2(i)) and pushes
   * it in as `window.aiConsentGranted`; the web app must not fall back to its
   * own localStorage flag there, or a declined user could still trigger a
   * Gemini request. On the web there is no native gate, so consent is handled
   * by the in-page modal.
   *
   * @returns {boolean}
   */
  hasAIConsent: () => {
    if (!isNativeApp()) {
      return true;
    }
    return window.aiConsentGranted === true;
  },

  /**
   * Ask the native shell to present its AI consent sheet.
   * @returns {boolean} - True if native handled
   */
  requestAIConsent: () => {
    if (window.webkit?.messageHandlers?.requestAIConsent) {
      window.webkit.messageHandlers.requestAIConsent.postMessage({});
      return true;
    }
    return false;
  },

  /**
   * Request notification permissions
   * @returns {boolean} - True if native handled
   */
  requestNotificationPermission: () => {
    if (window.webkit?.messageHandlers?.requestNotificationPermission) {
      window.webkit.messageHandlers.requestNotificationPermission.postMessage({});
      return true;
    }
    // Web fallback
    if ('Notification' in window) {
      Notification.requestPermission();
      return true;
    }
    return false;
  }
};

// Expose globally for debugging and native callbacks
window.NativeBridge = NativeBridge;

export default NativeBridge;
